import type { JsonObject, JsonValue } from "../types/json";

export function isJsonObject(value: JsonValue | undefined): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function asObject(value: JsonValue | undefined): JsonObject | undefined {
  return isJsonObject(value) ? value : undefined;
}

export function asArray(value: JsonValue | undefined): JsonValue[] {
  return Array.isArray(value) ? value : [];
}

export function asString(value: JsonValue | undefined): string {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return "";
}

export function getNested(object: JsonObject | undefined, ...keys: string[]): JsonValue | undefined {
  let current: JsonValue | undefined = object;

  for (const key of keys) {
    if (!isJsonObject(current)) return undefined;
    current = current[key];
  }

  return current;
}

export function isFilled(value: JsonValue): boolean {
  if (value === null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.some(isFilled);
  if (typeof value === "object") return Object.values(value).some(isFilled);
  return true;
}

export function countFields(value: JsonValue): { filled: number; total: number } {
  if (value === null) return { filled: 0, total: 1 };

  if (Array.isArray(value)) {
    return value.reduce<{ filled: number; total: number }>(
      (accumulator, item) => {
        const next = countFields(item);
        return {
          filled: accumulator.filled + next.filled,
          total: accumulator.total + next.total,
        };
      },
      { filled: 0, total: 0 },
    );
  }

  if (typeof value === "object") {
    const entries = Object.values(value);
    if (entries.length === 0) return { filled: 0, total: 1 };

    return entries.reduce<{ filled: number; total: number }>(
      (accumulator, item) => {
        const next = countFields(item);
        return {
          filled: accumulator.filled + next.filled,
          total: accumulator.total + next.total,
        };
      },
      { filled: 0, total: 0 },
    );
  }

  return { filled: isFilled(value) ? 1 : 0, total: 1 };
}
